import React from "react"
import { Control, Controller } from "react-hook-form"
import { DropDownList, IDropDownListProps } from "./DropDownList"
import { FloatingLabelInput } from "./FloatingLabelInput"
import { InputLabel } from "./InputLabel"
import "./MultiDropDownList.scss"

export interface IMultiDropDownListProps<T> extends IDropDownListProps<T> {
    control: Control
    defaultText: string
    label: string
    showInputForValue?: string
    name: string
    isRequired?: boolean
    missingInputValueErrorText?: string
    noValuesErrorText?: string
}

export interface IMultiDropDownListItem {
    value: string
    info?: string
}

export const MultiDropDownList = <T extends string>({
    options,
    control,
    defaultText,
    label,
    showInputForValue,
    name,
    isRequired,
    noValuesErrorText,
    missingInputValueErrorText,
    ...otherProps
}: IMultiDropDownListProps<T>) => {
    return (
        <div className="multi-drop-down-list">
            <InputLabel title={label}>
                <Controller
                    control={control}
                    name={name}
                    rules={{
                        validate: (data: IMultiDropDownListItem[] | undefined) => {
                            if (isRequired && (!data || data.length === 0)) {
                                return noValuesErrorText || false
                            }
                            if (showInputForValue) {
                                const missingInfo = !!data?.find((d) => d.value === showInputForValue && !d.info)
                                if (missingInfo) {
                                    return missingInputValueErrorText
                                }
                            }
                            return true
                        },
                    }}
                    render={({ onChange, onBlur, value, ref }) => {
                        const currentVal: IMultiDropDownListItem[] = value ?? []
                        return (
                            <div>
                                <DropDownList<string>
                                    {...otherProps}
                                    options={options.filter((o) => !currentVal.find((v) => v.value === o.value))}
                                    onChangeDropDown={(e, val) => {
                                        onChange([...currentVal, { value: val }])
                                    }}
                                    placeholder={defaultText}
                                />
                                <div className="multi-drop-down-list__value-container">
                                    {currentVal
                                        .filter((v) => v.value !== showInputForValue)
                                        .map((v: IMultiDropDownListItem, index: number) => (
                                            <div className="multi-drop-down-list__item">
                                                <span>{options.find((o) => o.value === v.value)?.text}</span>
                                                <span
                                                    className="multi-drop-down-list__delete-item"
                                                    onClick={() => {
                                                        onChange([...currentVal.slice(0, index), ...currentVal.slice(index + 1)])
                                                    }}
                                                >
                                                    {" "}
                                                    ×
                                                </span>
                                            </div>
                                        ))}
                                    {showInputForValue && currentVal.find((v) => v.value === showInputForValue) && (
                                        <div className="multi-drop-down-list__input-item">
                                            <InputLabel
                                                className="multi-drop-down-list__input-item-input"
                                                title={options.find((o) => o.value === showInputForValue)?.text}
                                            >
                                                <FloatingLabelInput
                                                    value={currentVal.find((v) => v.value === showInputForValue)?.info ?? ""}
                                                    onChange={(e) => {
                                                        onChange([
                                                            ...currentVal.filter((v) => v.value !== showInputForValue),
                                                            {
                                                                ...currentVal.find((v) => v.value === showInputForValue),
                                                                info: e.currentTarget.value,
                                                            },
                                                        ])
                                                    }}
                                                    hidePlaceholder
                                                />
                                            </InputLabel>
                                            <span
                                                className="multi-drop-down-list__delete-item"
                                                onClick={() => {
                                                    onChange([...currentVal.filter((v) => v.value !== showInputForValue)])
                                                }}
                                            >
                                                {" "}
                                                ×
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )
                    }}
                />
            </InputLabel>
        </div>
    )
}
